import React, { FunctionComponent as FC, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux'; // To call Action Creators
import { bindActionCreators } from 'redux';
import { actionCreators } from '../../../../state';
import { State } from '../../../../state';
import _ from 'lodash';
import * as d3 from  "d3";
import './index.scss';

import { impactEntityList, ImpactType } from '../../../../types/data.global';
import { MainViewTypes } from '../../../../types/data';
import CitationFlow from './CitationFlow';
import { selectReady, selectGraphInputs } from './selectors';


// edge bundling method
const d3ForceEdgeBundling = require('../util/d3-ForceEdgeBundling-improved.js');
// const edgeBundling = d3ForceEdgeBundling.edgeBundling;
const forceEdgeBundling = d3ForceEdgeBundling.ForceEdgeBundling;
const edgeBundling = (
  nodes: any, // Array of nodes including x and y coords e.g. [{id: "a", x: 10, y:10}, {id: "b", x: 20, y: 20}, ...]
  links: any, // Array of links in D3 forceSimulation format e.g. [{source: "a", target: "b"}, ... ]
  id = (d: any) => d.id,
  pathAttr = "path", // name of the attribute to save the paths

  // // case 1 - NIH (historical)
  // bundling_stiffness = 50, // 0.1, global bundling constant controlling edge stiffness
  // step_size = 0.05, // init. distance to move points
  // subdivision_rate = 2, // subdivision rate increase
  // cycles = 6, // 6, number of cycles to perform
  // iterations = 90, // 90, init. number of iterations for cycle
  // iterations_rate = 0.6666667, // rate at which iteration number decreases i.e. 2/3
  // compatibility_threshold = 0.45 // 0.65,  "which pairs of edges should be considered compatible (default is set to 0.6, 60% compatiblity)"

  // case 1 - NIH (prediction)
  bundling_stiffness = 50, // 0.1, global bundling constant controlling edge stiffness
  step_size = 0.05, // init. distance to move points
  subdivision_rate = 2, // subdivision rate increase
  cycles = 6, // 6, number of cycles to perform
  iterations = 90, // 90, init. number of iterations for cycle
  iterations_rate = 0.6666667, // rate at which iteration number decreases i.e. 2/3
  compatibility_threshold = 0.55 // 0.65,  "which pairs of edges should be considered compatible (default is set to 0.6, 60% compatiblity)"
) => {
  // The library wants the links as the index positions in the nodes array
  // console.log('nodes', nodes)
  // console.log('links', links)
  if (!nodes && !links) return;

  const dNodes = new Map(nodes.map((d: any, i: any) => [id(d), i]));
  const linksIs = links.map((l: any) => ({
    source: dNodes.get(typeof l.source === "object" ? id(l.source) : l.source),
    target: dNodes.get(typeof l.source === "object" ? id(l.target) : l.target)
  }));

  const edgeBundling = forceEdgeBundling()
    .nodes(nodes)
    .edges(linksIs)
    .bundling_stiffness(bundling_stiffness)
    .step_size(step_size)
    .subdivision_rate(subdivision_rate)
    .cycles(cycles)
    .iterations(iterations)
    .iterations_rate(iterations_rate)
    .compatibility_threshold(compatibility_threshold);

  edgeBundling.update = () => {
    const paths = edgeBundling();
    links.map((l: any, i: any) => (l[pathAttr] = paths[i]));
  };

  edgeBundling.update();

  return edgeBundling;
}

interface EntityFieldLevelCitationFlowProps {
  grantTypeNodeTransformXY: {
    x: number;
    y: number;
  },
  entityFieldGraphData: any;
}

const EntityFieldLevelCitationFlow: FC<EntityFieldLevelCitationFlowProps> = (props) => {

  const {
    grantTypeNodeTransformXY,
    entityFieldGraphData,
  } = props

  const dispatch = useDispatch()
  const {
    updateNodePositionList
  } = bindActionCreators(actionCreators, dispatch)

  const ready = useSelector(selectReady);
  const { leaf, centers, fieldData, grants } = useSelector(selectGraphInputs);


  const updatedEntityFieldData = fieldData;
  const leafNodePositionList = leaf;
  const bubbleTreemapEntityTypeCenterXYList = centers;
  const grantNodePositionList = grants;

  // const updatedEntityFieldData = useMemo(() => fieldData, [fieldData]);
  // const leafNodePositionList = useMemo(() => leaf, [leaf]);
  // const bubbleTreemapEntityTypeCenterXYList = useMemo(() => centers, [centers]);
  // const grantNodePositionList = useMemo(() => grants, [grants]);


  // data reducer
  // (1) data
  // const grantNodePositionList: MainViewTypes.GrantNodePosition[] = useSelector((state: State) => state.dataReducer?.grantNodePositionList) as MainViewTypes.GrantNodePosition[]
  // // const updatedEntityFieldDataRaw = entityFieldGraph_InputData
  // const updatedEntityFieldDataRaw: any[] = useSelector((state: State) => state.dataReducer?.entityFieldGraph_InputData) as any[]
  // const leafNodePosition_BubbleCenter: {
  //   leafNodePositionList: MainViewTypes.LeafNodePosition[];
  //   bubbleTreemapEntityTypeCenterXYList: MainViewTypes.BubbleTreemapCenterPositionWarpper[];
  // } = useSelector((state: State) => state.dataReducer?.leafNodePosition_BubbleCenter) as {
  //   leafNodePositionList: MainViewTypes.LeafNodePosition[];
  //   bubbleTreemapEntityTypeCenterXYList: MainViewTypes.BubbleTreemapCenterPositionWarpper[];
  // }
  const isEntityTypeGraphFinished: boolean = useSelector((state: State) => state.dataReducer?.isEntityTypeGraphFinished) as boolean
  const isInitializeBubbleTreemapFinished: boolean = useSelector((state: State) => state.uiReducer?.isInitializeBubbleTreemapFinished) as boolean
  const isOptimizedBubbleTreemapFinished: boolean = useSelector((state: State) => state.uiReducer?.isOptimizedBubbleTreemapFinished) as boolean
  // const isEntityFieldGraphFinished: boolean = useSelector((state: State) => state.dataReducer?.isEntityFieldGraphFinished) as boolean
  const isGrantTypeNodeFinished: boolean = useSelector((state: State) => state.dataReducer?.isDrawGrantTypeNodeFinished) as boolean

  // ui reducer
  const isShowHistoricalFlow: boolean = useSelector((state: State) => state.uiReducer?.isShowHistoricalFlow) as boolean
  const isShowPredictionFlow: boolean = useSelector((state: State) => state.uiReducer?.isShowPredictionFlow) as boolean
  // const svgSize = useSelector((state: State) => state.uiReducer?.svgSize) as { width: number, height: number }
  const svgW = useSelector((s: State) => s.uiReducer?.svgSize.width) as number;
  const svgH = useSelector((s: State) => s.uiReducer?.svgSize.height) as number;
  const impactStatisticsTypeList: string[] = useSelector((state: State) => state.uiReducer?.impactStatisticsTypeList) as string[]
  const showEntityTypeLevelGraph: boolean = useSelector((state: State) => state.uiReducer?.showEntityTypeLevelGraph) as boolean
  const showEntityFieldLevelGraph: boolean = useSelector((state: State) => state.uiReducer?.showEntityFieldLevelGraph) as boolean
  const impactType: string = useSelector((state: State) => state.uiReducer?.impactType) as string
  // const topKCitationFlow: number = useSelector((state: State) => state.uiReducer?.topKCitationFlow) as number
  // const highlghtedGrantFieldID: string = useSelector((state: State) => state.dataReducer?.highlghtedGrantFieldNode.grantFieldID) as string
  // const highlightedGrantFieldData: MainViewTypes.HighlightedGrantFieldData = useSelector((state: State) => state.dataReducer?.highlightedGrantFieldData) as MainViewTypes.HighlightedGrantFieldData



  console.log('\n\n========== [Step 4] Component: EntityFieldLevel_CitationFlow (started!) ============')


  // debug: check unnecessary re-rendering
  const renders = useRef(0);
  renders.current += 1;
  // console.log('[EntityFieldLevel_CitationFlow] renders =', renders.current);

  // preprocess
  const parts = impactType.split("_")
  const impactTypeRefined = parts.slice(0, 2).join("_")

  // opt 1: useMemo to make action references stable
  // 这些 selector 建议配合 shallowEqual（见文末）
  const entityTypeList = useMemo(() => impactStatisticsTypeList, [impactStatisticsTypeList]);
  // const updatedEntityFieldData = useMemo(() => updatedEntityFieldDataRaw, [updatedEntityFieldDataRaw]);
  // const leafNodePositionList = useMemo(
  //   () => leafNodePosition_BubbleCenter.leafNodePositionList,
  //   [leafNodePosition_BubbleCenter.leafNodePositionList]
  // );
  // const bubbleTreemapEntityTypeCenterXYList = useMemo(
  //   () => leafNodePosition_BubbleCenter.bubbleTreemapEntityTypeCenterXYList,
  //   [leafNodePosition_BubbleCenter.bubbleTreemapEntityTypeCenterXYList]
  // );
  // // // opt 2: original methods --- start ---
  // // // 这些新引用会让你后面所有 useMemo/计算都以为“变了”。
  // // const entityTypeList = _.cloneDeep(impactStatisticsTypeList)
  // // const updatedEntityFieldData = _.cloneDeep(updatedEntityFieldDataRaw)
  // // const leafNodePositionList = _.cloneDeep(leafNodePosition_BubbleCenter.leafNodePositionList)
  // // const bubbleTreemapEntityTypeCenterXYList = _.cloneDeep(leafNodePosition_BubbleCenter.bubbleTreemapEntityTypeCenterXYList)
  // // // opt 2: original methods --- end ---


  // opt 1: useMemo to make action references stable
  // 1) 计算 mainviewDataLink（筛选版）
  const mainviewDataLink = useMemo(() => {
    if (_.isEmpty(entityFieldGraphData)) return null;
    const parts = impactType.split("_");
    const impactTypeRefined = parts.slice(0, 2).join("_");

    let mv: any;
    if (impactType === ImpactType.INDIRECT_IMPACT_FOLDED) {
      let node_IndirectImpact_folded = _.omit(entityFieldGraphData[impactTypeRefined].node, 'paper');
      mv = {
        node: node_IndirectImpact_folded,
        link: entityFieldGraphData[impactTypeRefined].link.folded,
        grant_field_level_data: entityFieldGraphData[impactTypeRefined].grant_field_level_data,
      };
    } else if (impactType === ImpactType.DIRECT_IMPACT) {
      mv = entityFieldGraphData[impactType];
    } else {
      mv = {
        node: entityFieldGraphData[impactTypeRefined].node,
        link: entityFieldGraphData[impactTypeRefined].link.unfolded,
        grant_field_level_data: entityFieldGraphData[impactTypeRefined].grant_field_level_data,
      };
    }

    let link = mv?.link;
    if (!link) return null;

    const list = entityTypeList;
    if (impactType === ImpactType.DIRECT_IMPACT) {
      const keys = list.map(e => `grant-${e}`);
      link = _.pick(link, keys);
    } else if (impactType === ImpactType.INDIRECT_IMPACT_FOLDED) {
      const keys = list.map(e => `grant-${e}`);
      link = _.pick(link, keys);
    } else {
      const keys = [...list.map(e => `paper-${e}`), 'grant-paper'];
      link = _.pick(link, keys);
    }
    return link;
  }, [
    entityFieldGraphData, impactType, entityTypeList,
    // entityFieldGraphData, impactType, impactStatisticsTypeList, entityTypeList
  ]);


  // // opt 2: original methods --- start ---
  // // 1. filter entityFieldGraphData, based on selected `impactType` and `impactStatisticsTypeList`
  // let mainviewData: any = {}
  // let mainviewDataLink: any
  // if (!_.isEmpty(entityFieldGraphData)) {
  //   // 1. only for impactType === 'indirect_impact_folded': create a new object without the 'paper' key
  //   if (impactType === ImpactType.INDIRECT_IMPACT_FOLDED) {
  //     let node_IndirectImpact_folded = _.omit(entityFieldGraphData[impactTypeRefined].node, 'paper')
  //     mainviewData = {
  //       'node': node_IndirectImpact_folded,
  //       'link': entityFieldGraphData[impactTypeRefined].link.folded,
  //       'grant_field_level_data': entityFieldGraphData[impactTypeRefined].grant_field_level_data
  //     }
  //   } else if (impactType === ImpactType.DIRECT_IMPACT) {
  //     mainviewData = entityFieldGraphData[impactType]
  //   } else if (impactType === ImpactType.INDIRECT_IMPACT_UNFOLDED) {
  //     mainviewData = {
  //       'node': entityFieldGraphData[impactTypeRefined].node,
  //       'link': entityFieldGraphData[impactTypeRefined].link.unfolded,
  //       'grant_field_level_data': entityFieldGraphData[impactTypeRefined].grant_field_level_data, // temp: currently return folded link, TODO: add unfolded link
  //     }
  //   }
  //   mainviewDataLink = mainviewData.link // output for below code
  // }
  // // 2. filter mainviewDataLink, based on selected `impactStatisticsTypeList`
  // // console.log('mainviewData: ', mainviewData)
  // // console.log('mainviewDataLink: ', mainviewDataLink)
  // if (impactType === ImpactType.DIRECT_IMPACT) {
  //   const linkTypeList = entityTypeList.map((entity) => `grant-${entity}`) //  ['grant-paper', 'grant-patent', 'grant-clinical_trial']
  //   mainviewDataLink = _.pick(mainviewDataLink, linkTypeList)
  // } else if (impactType === ImpactType.INDIRECT_IMPACT_FOLDED) {
  //   const linkTypeList = entityTypeList.map((entity) => `grant-${entity}`) // ['grant-patent', 'grant-clinical_trial', 'grant-policy', 'grant-newsfeed']
  //   mainviewDataLink = _.pick(mainviewDataLink, linkTypeList)
  // } else if (impactType === ImpactType.INDIRECT_IMPACT_UNFOLDED) {
  //   const linkTypeList = entityTypeList.map((entity) => `paper-${entity}`) // ['grant-paper', 'paper-patent', 'paper-clinical_trial', 'paper-policy', 'paper-newsfeed']
  //   linkTypeList.push('grant-paper')
  //   mainviewDataLink = _.pick(mainviewDataLink, linkTypeList)
  // } else {
  //   console.log('error: unknown entity type, in Component: EntityFieldLevel_CitationFlow')
  // }
  // // opt 2: original methods --- end ---


  // console.log('\n\n=> after, mainviewDataLink: ', mainviewDataLink)

  // entityTypeList.push('grant')
  // let nodesFiltered = entityTypeGraph_InputData?.nodes.filter((node: MainViewTypes.EntityTypeNode) => entityTypeList.includes(node.id))
  // let linksFiltered = entityTypeGraph_InputData?.links.filter((link: MainViewTypes.EntityTypeLink) => entityTypeList.includes(link.source.id) && entityTypeList.includes(link.target.id))
  // entityTypeGraph_InputData = {
  //   nodes: nodesFiltered,
  //   links: linksFiltered,
  // }
  // console.log('entityTypeList: ', entityTypeList)


  // 4. entity-field level (hierarchical edge bundling, customized version)
  // 4-1. input
  // (1) node data (with node positions from step 2 - circle packing)
  // console.log('edgeEntityTypeData: ', edgeEntityTypeData) // impact field node用这个bubble treemap
  // console.log('grantNodePositionList: ', grantNodePositionList) // grant field node用这个单独生成的(x, y)
  // console.log('bubbleTreemapEntityTypeCenterXYList: ', bubbleTreemapEntityTypeCenterXYList)

  // opt 1: useMemo to make action references stable --- start ---
  // 依赖 svgSize 的渲染函数用 useCallback
  const drawGraph3 = useCallback((nodes: any[], links: any[]) => {
    const x = d3.scaleLinear()
      // @ts-ignore
      .domain(d3.extent(nodes, d => d.x))
      .range([0, svgW])
      .nice();

    const y = d3.scaleLinear()
      // @ts-ignore
      .domain(d3.extent(nodes, d => d.y))
      .range([0, svgH])
      .nice();

    const line = d3.line<any>()
      .x(d => d.x)
      .y(d => d.y);

    return links.map((link: any) => ({
      path: line(link.path),
      num_citation: link.num_citation,
      source: link.source,
      target: link.target,
      linkType: link.linkType,
    }));
  }, [svgW, svgH]);

  // 2) 由 redux/state 计算节点坐标（稳定引用）
  const nodePositionList: MainViewTypes.NodePosition[] = useMemo(() => {
    if (!leafNodePositionList?.length || !bubbleTreemapEntityTypeCenterXYList?.length || !updatedEntityFieldData?.length) {
      return [];
    }

    const centersByEntity = new Map(
      bubbleTreemapEntityTypeCenterXYList.map(d => [d.entityType, d.center])
    );

    const pos: MainViewTypes.NodePosition[] = [];

    // impact leaf nodes
    leafNodePositionList.forEach((n) => {
      const et = n.data.entityType;
      const center = centersByEntity.get(et);
      if (!center) return;

      const etNode = updatedEntityFieldData.find(e => e.id === et || e.name === et);
      if (!etNode) return;

      pos.push({
        id: n.data.name,
        x: n.x + (-center.x + etNode.x),
        y: n.y + (-center.y + etNode.y),
        r: n.r,
        data: n.data,
      });
    });

    // grant nodes
    grantNodePositionList.forEach((g) => {
      pos.push({
        id: g.grant_field_code,
        x: g.x + grantTypeNodeTransformXY.x,
        y: g.y + grantTypeNodeTransformXY.y,
        r: g.radius,
        data: {
          name: g.grant_field_code,
          value: -1,
          entityType: 'grant',
        },
      });
    });

    return pos;
  }, [
    leafNodePositionList,
    bubbleTreemapEntityTypeCenterXYList,
    updatedEntityFieldData,
    grantNodePositionList,
    grantTypeNodeTransformXY.x,
    grantTypeNodeTransformXY.y,
  ]);

  // 3) 由 backend link 数据计算边（稳定引用）
  const leafNodeEdgeData: any[] = useMemo(() => {
    if (!mainviewDataLink) return [];
    const edges: any[] = [];

    _.forEach(mainviewDataLink, (linkTypeData: any, linkType: string) => {
      let list: any[] = linkTypeData.edge_list || [];
      list = list.map(l => ({
        ...l,
        linkType,
      }));
      edges.push(...list);
    });

    // 统一成字符串 id，并附上 value
    return edges.map(item => ({
      ...item,
      value: item.num_citation,
      source: typeof item.source === 'string' ? item.source : Math.floor(item.source).toString(),
      target: typeof item.target === 'string' ? item.target : Math.floor(item.target).toString(),
    }));
  }, [mainviewDataLink]);

  // // 4) 是否满足计算路径的前置条件
  // const ready = useMemo(() => {
  //   return (
  //     (isShowHistoricalFlow || isShowPredictionFlow) &&
  //     isGrantTypeNodeFinished &&
  //     isEntityTypeGraphFinished &&
  //     isInitializeBubbleTreemapFinished &&
  //     isOptimizedBubbleTreemapFinished &&
  //     nodePositionList.length > 0 &&
  //     leafNodeEdgeData.length > 0
  //   );
  // }, [
  //   isShowHistoricalFlow,
  //   isShowPredictionFlow,
  //   isGrantTypeNodeFinished,
  //   isEntityTypeGraphFinished,
  //   isInitializeBubbleTreemapFinished,
  //   isOptimizedBubbleTreemapFinished,
  //   nodePositionList.length,
  //   leafNodeEdgeData.length,
  // ]);


  // === working copies for bundler（可变）===
  // 这两份副本是全新的可变对象，bundler 写 x/y（在 subdivision 里克隆成点后迭代）和写 link.path 都不会触到 Redux/selector 的只读对象。
  const nodesForBundle = useMemo(() => {
    // 只拷贝 bundler 必要的字段，确保是可写的 plain 对象
    return nodePositionList.map(n => ({
      id: n.id,
      x: +n.x,
      y: +n.y,
    }));
  }, [nodePositionList]);

  const linksForBundle = useMemo(() => {
    return leafNodeEdgeData.map(l => ({
      // bundler 会把 path 写回 link 对象，所以也要用副本
      source: typeof l.source === 'object' ? l.source.id : l.source,
      target: typeof l.target === 'object' ? l.target.id : l.target,
      num_citation: l.num_citation,
      linkType: l.linkType,
    }));
  }, [leafNodeEdgeData]);




  // 5) 跑 bundling，并生成可渲染的路径（仅在依赖变化时）
  const linkPathList: MainViewTypes.CitationFlowPathUnit[] = useMemo(() => {
    if (!ready) return [];
    // 在可变副本上运行 bundler
    edgeBundling(nodesForBundle, linksForBundle);
    // 基于副本生成 path（linksForBundle 里已写入 link.path）
    return drawGraph3(nodesForBundle, linksForBundle);
  }, [ready, nodesForBundle, linksForBundle, drawGraph3]);
  // const linkPathList: MainViewTypes.CitationFlowPathUnit[] = useMemo(() => {
  //   if (!ready) return [];
  //   // 写入 link.path（就地修改 leafNodeEdgeData）
  //   edgeBundling(nodePositionList, leafNodeEdgeData);
  //   // 基于 svgSize 生成最终 path 字符串
  //   return drawGraph3(nodePositionList, leafNodeEdgeData);
  // }, [ready, nodePositionList, leafNodeEdgeData, drawGraph3]);

  // 6) 把节点位置同步回 redux（只在 nodePositionList 真变时）
  const prevNodePosRef = useRef<MainViewTypes.NodePosition[]>();
  useEffect(() => {
    if (nodePositionList.length === 0) return;
    if (_.isEqual(prevNodePosRef.current, nodePositionList)) return;
    prevNodePosRef.current = nodePositionList;
    updateNodePositionList(nodePositionList);
  }, [nodePositionList, updateNodePositionList]);
  // useEffect(() => {
  //   if (nodePositionList.length > 0) {
  //     updateNodePositionList(nodePositionList);
  //   }
  // }, [nodePositionList, updateNodePositionList]);
  // opt 1: useMemo to make action references stable --- end ---



  // // opt 2: original methods --- start ---
  // // preprocess data: flatten node positions
  // const nodePositionList: MainViewTypes.NodePosition[] = []
  // let linkPathList: MainViewTypes.CitationFlowPathUnit[] = []
  // let leafNodeEdgeData: any[] = []
  // // let nodeElementList: JSX.Element[] = []
  // // let linkElementList: JSX.Element[] = []

  // const edgeBundling = (
  //   nodes: any, // Array of nodes including x and y coords e.g. [{id: "a", x: 10, y:10}, {id: "b", x: 20, y: 20}, ...]
  //   links: any, // Array of links in D3 forceSimulation format e.g. [{source: "a", target: "b"}, ... ]
  //   id = (d: any) => d.id,
  //   pathAttr = "path", // name of the attribute to save the paths

  //   // // case 1 - NIH (historical)
  //   // bundling_stiffness = 50, // 0.1, global bundling constant controlling edge stiffness
  //   // step_size = 0.05, // init. distance to move points
  //   // subdivision_rate = 2, // subdivision rate increase
  //   // cycles = 6, // 6, number of cycles to perform
  //   // iterations = 90, // 90, init. number of iterations for cycle
  //   // iterations_rate = 0.6666667, // rate at which iteration number decreases i.e. 2/3
  //   // compatibility_threshold = 0.45 // 0.65,  "which pairs of edges should be considered compatible (default is set to 0.6, 60% compatiblity)"

  //   // case 1 - NIH (prediction)
  //   bundling_stiffness = 50, // 0.1, global bundling constant controlling edge stiffness
  //   step_size = 0.05, // init. distance to move points
  //   subdivision_rate = 2, // subdivision rate increase
  //   cycles = 6, // 6, number of cycles to perform
  //   iterations = 90, // 90, init. number of iterations for cycle
  //   iterations_rate = 0.6666667, // rate at which iteration number decreases i.e. 2/3
  //   compatibility_threshold = 0.55 // 0.65,  "which pairs of edges should be considered compatible (default is set to 0.6, 60% compatiblity)"
  // ) => {
  //   // The library wants the links as the index positions in the nodes array
  //   // console.log('nodes', nodes)
  //   // console.log('links', links)
  //   if (!nodes && !links) return;

  //   const dNodes = new Map(nodes.map((d: any, i: any) => [id(d), i]));
  //   const linksIs = links.map((l: any) => ({
  //     source: dNodes.get(typeof l.source === "object" ? id(l.source) : l.source),
  //     target: dNodes.get(typeof l.source === "object" ? id(l.target) : l.target)
  //   }));

  //   const edgeBundling = forceEdgeBundling()
  //     .nodes(nodes)
  //     .edges(linksIs)
  //     .bundling_stiffness(bundling_stiffness)
  //     .step_size(step_size)
  //     .subdivision_rate(subdivision_rate)
  //     .cycles(cycles)
  //     .iterations(iterations)
  //     .iterations_rate(iterations_rate)
  //     .compatibility_threshold(compatibility_threshold);

  //   edgeBundling.update = () => {
  //     const paths = edgeBundling();
  //     links.map((l: any, i: any) => (l[pathAttr] = paths[i]));
  //   };

  //   edgeBundling.update();

  //   return edgeBundling;
  // }
  // const drawGraph3 = (nodes: any[], links: any[]) => {
  //   const useEdgeBundling = true;
  //   const x = d3
  //     .scaleLinear()
  //     // @ts-ignore
  //     .domain(d3.extent(nodes, (d) => d.x))
  //     .range([0, svgSize.width])
  //     // .range([0, svgSize.mainview.width])
  //     .nice();
  //   const y = d3
  //     .scaleLinear()
  //     // @ts-ignore
  //     .domain(d3.extent(nodes, (d) => d.y))
  //     .range([0, svgSize.height])
  //     // .range([0, svgSize.mainview.height])
  //     .nice();
  //   const line = d3
  //     .line()
  //     .x((d: any) => d.x)
  //     .y((d: any) => d.y);
  //     // .x((d: any) => x(d.x))
  //     // .y((d: any) => y(d.y));

  //   const lineGenerator = d3.line()
  //     .curve(d3.curveBasis);
  //   if (useEdgeBundling) {
  //     return links.map((link) => {
  //       // console.log('link: ', link)
  //       return {
  //         path: line(link.path), // 直线
  //         // path: lineGenerator(link.path), //曲线
  //         num_citation: link.num_citation,
  //         source: link.source,
  //         target: link.target,
  //         linkType: link.linkType,
  //       }
  //     })
  //   } else {
  //     return []
  //     // const nodesMap = new Map(nodes.map(d => [d.id, d]))
  //     // svg
  //     //   .selectAll("line")
  //     //   .data(links)
  //     //   .join("line")
  //     //   .attr("x1", (d) => x(nodesMap.get(d.source).x))
  //     //   .attr("y1", (d) => y(nodesMap.get(d.source).y))
  //     //   .attr("x2", (d) => x(nodesMap.get(d.target).x))
  //     //   .attr("y2", (d) => y(nodesMap.get(d.target).y))
  //     //   .attr("fill", "none")
  //     //   .attr("stroke", "#aaa3");
  //   }
  //   // return svg.node();
  // }
  // // console.log('--------isGrantTypeNodeFinished: ', isGrantTypeNodeFinished)
  // // console.log('--------isEntityTypeGraphFinished: ', isEntityTypeGraphFinished)
  // // // console.log('--------isEntityFieldGraphFinished:', isEntityFieldGraphFinished)
  // // console.log('--------showEntityTypeLevelGraph: ', showEntityTypeLevelGraph)
  // // console.log('--------showEntityFieldLevelGraph: ', showEntityFieldLevelGraph)
  // // console.log((!showEntityTypeLevelGraph) && (showEntityFieldLevelGraph) && (isGrantTypeNodeFinished) && (isEntityTypeGraphFinished) && (isInitializeBubbleTreemapFinished && isOptimizedBubbleTreemapFinished))
  // if(isShowHistoricalFlow || isShowPredictionFlow) {
  // // if ((!showEntityTypeLevelGraph) && (showEntityFieldLevelGraph) && (isGrantTypeNodeFinished) && (isEntityTypeGraphFinished) && (isInitializeBubbleTreemapFinished && isOptimizedBubbleTreemapFinished)) {
  // // if ((!showEntityTypeLevelGraph) && (showEntityFieldLevelGraph) && (isGrantTypeNodeFinished) && (isEntityTypeGraphFinished) && (isEntityFieldGraphFinished)) {
  //   // (1) calc nodePositionList
  //   // opt 1: global node_size scale for bubble treemap leaf nodes
  //   leafNodePositionList.forEach((nodeData) => {
  //     const entityType = nodeData.data.entityType
  //     const center = _.find(bubbleTreemapEntityTypeCenterXYList, { entityType: entityType })
  //     const centerX = center?.center.x as number
  //     const centerY = center?.center.y as number
  //     // find entity type force-directed graph node position
  //     const entityTypeGraph_NodePosition = updatedEntityFieldData.find(entity => entity.id === entityType)
  //     // // const entityTypeGraph_NodePosition = updatedEntityFieldData.find(entity => entity.name === entityType)
  //     // console.log('entityType: ', entityType)
  //     // console.log('updatedEntityFieldData: ', updatedEntityFieldData)
  //     // console.log('entityTypeGraph_NodePosition: ', entityTypeGraph_NodePosition)
  //     nodePositionList.push({
  //       id: nodeData.data.name,
  //       x: nodeData.x + (-centerX + entityTypeGraph_NodePosition.x),
  //       y: nodeData.y + (-centerY + entityTypeGraph_NodePosition.y),
  //       // x: nodeData.x, // + (-centerX + entityTypeGraph_NodePosition.x),
  //       // y: nodeData.y, // + (-centerY + entityTypeGraph_NodePosition.y),
  //       r: nodeData.r,
  //       data: nodeData.data,
  //     })
  //   })
  //   grantNodePositionList.forEach((grantNodeData) => {
  //     nodePositionList.push({
  //       id: grantNodeData.grant_field_code,
  //       x: grantNodeData.x + grantTypeNodeTransformXY.x,
  //       y: grantNodeData.y + grantTypeNodeTransformXY.y,
  //       r: grantNodeData.radius,
  //       data: {
  //         name: grantNodeData.grant_field_code,
  //         // name: grantNodeData.grant_field_name,
  //         value: -1, // note: 这个结构跟上面edgeEntityTypeData的结构不一样，但应该不影响
  //         entityType: 'grant',
  //       },
  //     })
  //   })
  //   // opt 2: seperate node_size scale for bubble treemap leaf nodes
  //   // edgeEntityTypeData.forEach((entityTypeNodeData: any) => {
  //   //   entityTypeNodeData.leafNodes.forEach((node: any) => {
  //   //     if (node.data.entity === 'grant') {
  //   //       // pass. for grant field node, use `grantNodePositionList` precomputed result
  //   //     } else {
  //   //       nodePositionList.push({
  //   //         id: node.data.name,
  //   //         x: node.x,
  //   //         y: node.y,
  //   //         r: node.r,
  //   //         // value: node.data.value,
  //   //         data: node.data,
  //   //       })
  //   //     }
  //   //   })
  //   // })
  //   // grantNodePositionList.forEach((grantNodeData) => {
  //   //   nodePositionList.push({
  //   //     id: grantNodeData.grant_field_code,
  //   //     x: grantNodeData.x + grantTypeNodeTransformXY.x,
  //   //     y: grantNodeData.y + grantTypeNodeTransformXY.y,
  //   //     r: grantNodeData.radius,
  //   //     data: grantNodeData.grant_field_name, // note: 这个结构跟上面edgeEntityTypeData的结构不一样，但应该不影响
  //   //   })
  //   // })
  //   // console.log('nodePositionList: ', nodePositionList)


  //   // (2) edge data from backend
  //   if (mainviewDataLink) {
  //     _.forEach(mainviewDataLink, (linkTypeData: any, linkType: string) => {
  //       let linkList: any[] = linkTypeData.edge_list
  //       // add linkType to linkList
  //       linkList = linkList.map((link: any) => ({
  //         ...link,
  //         linkType: linkType
  //       }))
  //       leafNodeEdgeData.push(...linkList)
  //     })
  //   }
  //   // console.log('leafNodeEdgeData: ', leafNodeEdgeData)
  //   // preprocess data: transform id (int) into string
  //   leafNodeEdgeData = leafNodeEdgeData.map(item => ({
  //     ...item,
  //     value: item.num_citation,
  //     source: Math.floor(item.source).toString(),
  //     target: typeof item.target === 'string' ? item.target : Math.floor(item.target).toString()
  //   }))
  //   // console.log('leafNodeEdgeData', leafNodeEdgeData)

  //   // // 4-2. draw links
  //   // // method 1: straight line (test)
  //   // const linkElementList: JSX.Element[] = leafNodeEdgeData.map((item, index) => {
  //   //   const sourceNode = nodePositionList.find(node => node.id === item.source)
  //   //   const targetNode = nodePositionList.find(node => node.id === item.target)
  //   //   const linkPath = `M${sourceNode.x},${sourceNode.y}L${targetNode.x},${targetNode.y}`
  //   //   const linkWidth = linkScale(item.num_citation)
  //   //   return (
  //   //     <path key={index} d={linkPath} stroke='red' strokeWidth={linkWidth} fill='none' opacity={0.3} />
  //   //   )
  //   // })


  //   // // // method 2: force bundle
  //   // // console.log('nodeElement: ', _.sortBy(nodes4Edge.map(node => node.id)))
  //   // // console.log('edge, grant_node: ', _.sortBy(_.uniq(_.map(leafNodeEdgeData, 'source'))))
  //   // // console.log('same?: ', _.isEqual(_.sortBy(nodes4Edge.map(node => node.id)), _.sortBy(_.uniq(_.map(leafNodeEdgeData, 'source')))))
  //   // console.log('isGrantTypeNodeFinished: ', isGrantTypeNodeFinished)
  //   // console.log('isEntityTypeGraphFinished: ', isEntityTypeGraphFinished)
  //   // console.log('isEntityFieldGraphFinished:', isEntityFieldGraphFinished)
  //   // console.log('nodePositionList: ', nodePositionList)
  //   // console.log('leafNodeEdgeData: ', leafNodeEdgeData)
  //   if ((isGrantTypeNodeFinished) && (isEntityTypeGraphFinished) && (isInitializeBubbleTreemapFinished && isOptimizedBubbleTreemapFinished) && (nodePositionList.length > 0) && (leafNodeEdgeData.length > 0)) {
  //   // if ((isGrantTypeNodeFinished) && (isEntityTypeGraphFinished) && (isEntityFieldGraphFinished) && (nodePositionList.length > 0) && (leafNodeEdgeData.length > 0)) {
  //     // console.log('start edge bundling!')

  //     // 2-1. run force bundle
  //     const compatibility_threshold = 0.2,
  //     bundling_stiffness = 50,
  //     step_size = 0.06;
  //     const bundling = edgeBundling(
  //       nodePositionList, leafNodeEdgeData
  //       // nodePositionList, leafNodeEdgeData,
  //       // compatibility_threshold,
  //       // bundling_stiffness,
  //       // step_size
  //       // // @ts-ignore
  //       // { nodes4Edge, leafNodeEdgeData },
  //       // {
  //       //   compatibility_threshold,
  //       //   bundling_stiffness,
  //       //   step_size
  //       // }
  //     );
  //     // then links will contain the bundles in the path attribute
  //     // console.log('==> leafNodeEdgeData: ', leafNodeEdgeData)
  //     linkPathList = drawGraph3(nodePositionList, leafNodeEdgeData)

  //     // // //////////////////  2-2 & 2-3: move to the child component (i.e., CitationFlow), avoid highlighting to re-render/re-run force-bundle (start)  ///////////////
  //     // // 2-2. draw link element
  //     // // input
  //     // console.log('linkPathList: ', linkPathList)
  //     // // console.log('highlghtedGrantFieldID: ', highlghtedGrantFieldID)
  //     // let highlightedGrantNode_linkPathList = []
  //     // if ((highlghtedGrantFieldID !== '') && (linkPathList.length > 0)) {
  //     //   // 1. filter this grant node's all links
  //     //   highlightedGrantNode_linkPathList = linkPathList.filter(linkData => (linkData.source === highlghtedGrantFieldID))

  //     //   // 2. sort links by value (num_citation)
  //     //   highlightedGrantNode_linkPathList = _.sortBy(highlightedGrantNode_linkPathList, 'num_citation').reverse()
  //     //   console.log('highlightedGrantNode_linkPathList: ', highlightedGrantNode_linkPathList)

  //     //   // 3. only draw topK links
  //     //   const topKLinks = highlightedGrantNode_linkPathList.slice(0, topKCitationFlow)

  //     //   linkElementList = topKLinks.map((linkData: any) => {
  //     //     return <path id={`citation-link-${linkData.source}-${linkData.target}`} d={linkData.path} stroke="#5d8485" strokeWidth={linkScale(linkData.num_citation)} fill="none" opacity={0.3} />
  //     //   })
  //     // }
  //     // // 2-3. draw node element (only for debug)
  //     // nodeElementList = nodePositionList.map((node) => (
  //     //   <circle cx={node.x} cy={node.y} r={node.r} fill='none' opacity={0.5} />
  //     // ))
  //     // // //////////////////  2-2 & 2-3: move to the child component, avoid highlighting to re-render/re-run force-bundle (end)  ///////////////


  //     // (Optional) To update the bundling call, useful for force simulations
  //     bundling.update();
  //   }
  //   // update nodePositionList to state, for highlight impact node, when clickling a grant field node
  //   updateNodePositionList(nodePositionList)
  //   // console.log('nodePositionList: ', nodePositionList)
  //   // console.log('nodeElementList: ', nodeElementList)
  //   // console.log('linkElementList: ', linkElementList)

  //   // method 3: customized curve
  //   // TODO
  // }
  // // opt 2: original methods --- end ---


  // // debug: check unnecessary re-rendering
  // useEffect(() => console.log('dep: svgW/H changed', svgW, svgH), [svgW, svgH]);
  // useEffect(() => console.log('dep: mainviewDataLink changed'), [mainviewDataLink]);
  // useEffect(() => console.log('dep: nodePositionList changed (ref)'), [nodePositionList]);
  // useEffect(() => console.log('dep: leafNodeEdgeData changed (ref)'), [leafNodeEdgeData]);
  // // 1) 把所有 boolean flag 打点
  // // 既然 selectReady 已把 6 个 flag 合并，你可以只打一个点：
  // useEffect(() => console.log('dep: ready =', ready), [ready]);
  // // useEffect(() => console.log('dep: flags',
  // //   {isShowHistoricalFlow, isShowPredictionFlow,
  // //   isGrantTypeNodeFinished, isEntityTypeGraphFinished,
  // //   isInitializeBubbleTreemapFinished, isOptimizedBubbleTreemapFinished}
  // // ), [
  // //   isShowHistoricalFlow, isShowPredictionFlow,
  // //   isGrantTypeNodeFinished, isEntityTypeGraphFinished,
  // //   isInitializeBubbleTreemapFinished, isOptimizedBubbleTreemapFinished
  // // ]);
  // // 2) 对这些 selector 本体打点（数组/对象的“引用变化”）
  // useEffect(() => console.log('dep: grantNodePositionList (ref)'), [grantNodePositionList]);
  // // useEffect(() => console.log('dep: leafNodePosition_BubbleCenter (ref)'), [leafNodePosition_BubbleCenter]);
  // // useEffect(() => console.log('dep: updatedEntityFieldDataRaw (ref)'), [updatedEntityFieldDataRaw]);
  // // 3) 对 props 打点
  // useEffect(() => console.log('dep: props.grantTypeNodeTransformXY', grantTypeNodeTransformXY), [grantTypeNodeTransformXY]);
  // useEffect(() => console.log('dep: props.entityFieldGraphData (ref)'), [entityFieldGraphData]);


  // console.log('========== [Step 4] Component: EntityFieldLevel_CitationFlow (finished!) ============\n\n')
  // // console.log('linkPathList: ', linkPathList)


  return (
    <>
      <CitationFlow linkPathList={linkPathList} nodePositionList={nodePositionList} leafNodeEdgeData={leafNodeEdgeData}/>
      {/* {nodeElementList}
      {linkElementList} */}
    </>
  )
}

function areEqual(prev: EntityFieldLevelCitationFlowProps, next: EntityFieldLevelCitationFlowProps) {
  if (prev.grantTypeNodeTransformXY.x !== next.grantTypeNodeTransformXY.x) return false;
  if (prev.grantTypeNodeTransformXY.y !== next.grantTypeNodeTransformXY.y) return false;
  return prev.entityFieldGraphData === next.entityFieldGraphData;
}
export default React.memo(EntityFieldLevelCitationFlow, areEqual);

// export default EntityFieldLevelCitationFlow;